import React, { createContext, useContext, useState } from 'react';

// Estado inicial das notificações
const initialState = {
  notifications: [],
  open: false,
  message: '',
  severity: 'info', // 'success', 'error', 'warning', 'info'
  autoHideDuration: 6000,
};

// Contexto de notificações
const NotificationContext = createContext();

// Hook personalizado para usar o contexto
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification deve ser usado dentro de um NotificationProvider');
  }
  return context;
};

// Provider do contexto de notificações
export const NotificationProvider = ({ children }) => {
  const [state, setState] = useState(initialState);

  // Função para mostrar notificação
  const showNotification = (message, severity = 'info', autoHideDuration = 6000) => {
    setState(prevState => ({
      ...prevState,
      open: true,
      message,
      severity,
      autoHideDuration,
    }));
  };

  // Função para fechar notificação
  const hideNotification = () => {
    setState(prevState => ({
      ...prevState,
      open: false,
    }));
  };

  // Funções de conveniência para diferentes tipos de notificação
  const showSuccess = (message, autoHideDuration) => {
    showNotification(message, 'success', autoHideDuration);
  };

  const showError = (message, autoHideDuration) => {
    showNotification(message, 'error', autoHideDuration);
  };

  const showWarning = (message, autoHideDuration) => {
    showNotification(message, 'warning', autoHideDuration);
  };

  const showInfo = (message, autoHideDuration) => {
    showNotification(message, 'info', autoHideDuration);
  };

  // Função para adicionar notificação à lista (para múltiplas notificações futuras)
  const addNotification = (notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      ...notification,
      timestamp: new Date(),
    };

    setState(prevState => ({
      ...prevState,
      notifications: [...prevState.notifications, newNotification],
    }));

    // Auto-remover após um tempo
    setTimeout(() => {
      removeNotification(id);
    }, notification.autoHideDuration || 6000);

    return id;
  };

  // Função para remover notificação específica
  const removeNotification = (id) => {
    setState(prevState => ({
      ...prevState,
      notifications: prevState.notifications.filter(notification => notification.id !== id),
    }));
  };

  // Função para limpar todas as notificações
  const clearAllNotifications = () => {
    setState(prevState => ({
      ...prevState,
      notifications: [],
    }));
  };

  // Função para mostrar notificação de loading
  const showLoading = (message = 'Carregando...') => {
    showNotification(message, 'info', 0); // 0 = não remove automaticamente
  };

  // Função para mostrar notificação de sucesso de operação
  const showOperationSuccess = (operation = 'Operação') => {
    showSuccess(`${operation} realizada com sucesso!`);
  };

  // Função para mostrar notificação de erro de operação
  const showOperationError = (operation = 'Operação', error = '') => {
    const message = error ? `Erro ao realizar ${operation.toLowerCase()}: ${error}` : `Erro ao realizar ${operation.toLowerCase()}`;
    showError(message);
  };

  // Valor do contexto
  const value = {
    ...state,
    showNotification,
    hideNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    addNotification,
    removeNotification,
    clearAllNotifications,
    showLoading,
    showOperationSuccess,
    showOperationError,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
